/**
 * Client-side API wrapper
 * Handles communication with backend API endpoints
 * Falls back to mock data when API is unavailable
 */

import type { QuoteCard, SearchResponse, HealthResponse, LegacyQuoteCard } from '@/types';
import { mockQuoteCards, searchResults } from '@/data/mockData';

const API_BASE_URL = '/api';

// Data mode from environment
const DATA_MODE = import.meta.env.VITE_DATA_MODE || 'hybrid';

/**
 * Check if we should use mock data
 */
function shouldUseMock(): boolean {
  return DATA_MODE === 'mock';
}

/**
 * Health check
 */
export async function health(): Promise<HealthResponse | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    if (!response.ok) return null;
    return (await response.json()) as HealthResponse;
  } catch {
    return null;
  }
}

/**
 * Search for quote cards
 */
export async function search(params: {
  q: string;
  mode?: 'stack' | 'timeline' | 'argument';
  lane?: 'A' | 'B' | 'C' | 'ALL';
  timeFrom?: string;
  timeTo?: string;
  limit?: number;
}): Promise<SearchResponse> {
  // Use mock data if configured
  if (shouldUseMock()) {
    return searchMockData(params);
  }

  try {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);
    if (params.mode) searchParams.set('mode', params.mode);
    if (params.lane) searchParams.set('lane', params.lane);
    if (params.timeFrom) searchParams.set('timeFrom', params.timeFrom);
    if (params.timeTo) searchParams.set('timeTo', params.timeTo);
    if (params.limit) searchParams.set('limit', String(params.limit));

    const response = await fetch(`${API_BASE_URL}/search?${searchParams.toString()}`);

    if (!response.ok) {
      // Fallback to mock in hybrid mode
      if (DATA_MODE === 'hybrid') {
        return searchMockData(params);
      }
      throw new Error(`Search failed: ${response.status}`);
    }

    return (await response.json()) as SearchResponse;
  } catch (error) {
    // Fallback to mock in hybrid mode
    if (DATA_MODE === 'hybrid') {
      return searchMockData(params);
    }

    console.error('[API] Search error:', error);
    throw error;
  }
}

/**
 * Get a single card by ID
 */
export async function getCard(id: string): Promise<QuoteCard | null> {
  if (shouldUseMock()) {
    return getMockCard(id);
  }

  try {
    const response = await fetch(`${API_BASE_URL}/card?id=${encodeURIComponent(id)}`);

    if (!response.ok) {
      if (DATA_MODE === 'hybrid') {
        return getMockCard(id);
      }
      return null;
    }

    return (await response.json()) as QuoteCard;
  } catch (error) {
    if (DATA_MODE === 'hybrid') {
      return getMockCard(id);
    }

    console.error('[API] Get card error:', error);
    return null;
  }
}

/**
 * Search mock data locally
 */
function searchMockData(params: {
  q: string;
  lane?: 'A' | 'B' | 'C' | 'ALL';
  limit?: number;
}): SearchResponse {
  const query = params.q.toLowerCase();
  const lane = params.lane || 'ALL';
  const limit = params.limit || 20;
  const curatedResults = searchResults[query];
  const queryTokens = query
    .split(/\s+/)
    .map((token) => token.replace(/[^a-z0-9]/g, ''))
    .filter((token) => token.length >= 3);

  const filtered = (curatedResults ?? mockQuoteCards).filter((card) => {
    // Filter by lane
    if (lane !== 'ALL' && card.lane !== lane) return false;

    if (curatedResults) return true;

    // Filter by query
    const searchableText =
      `${card.quote} ${card.source} ${card.documentTitle} ${card.context ?? ''}`.toLowerCase();
    const matches = queryTokens.some((token) => searchableText.includes(token));

    return matches;
  });

  // Normalize to QuoteCard format
  const cards: QuoteCard[] = filtered.slice(0, limit).map((card) => ({
    id: card.id,
    lane: card.lane,
    sourceType: 'mock',
    sourceName: card.source,
    title: card.documentTitle,
    date: card.date,
    quote: card.quote,
    url: card.canonicalLink,
    context: card.context,
    meta: {
      effectiveDate: card.effectiveDate,
      amendedBy: card.amendedBy,
      supersededBy: card.supersededBy,
    },
    stance: 'mixed',
  }));

  return {
    query: params.q,
    cards,
    total: cards.length,
    sources: ['mock'],
    answer: {
      status: cards.length > 0 ? 'context' : 'insufficient',
      shortAnswer: cards.length > 0 ? 'Demo evidence found' : 'No verified answer yet',
      text:
        cards.length > 0
          ? 'These demonstration records show how Duly Cited presents source-backed evidence.'
          : 'The demonstration data does not directly answer this question.',
      confidence: cards.length > 0 ? 'medium' : 'low',
      citationCardIds: cards[0] ? [cards[0].id] : [],
      sourceName: cards[0]?.sourceName,
      sourceUrl: cards[0]?.url,
    },
  };
}

/**
 * Get mock card by ID
 */
function getMockCard(id: string): QuoteCard | null {
  const card = mockQuoteCards.find((c) => c.id === id);
  if (!card) return null;

  return {
    id: card.id,
    lane: card.lane,
    sourceType: 'mock',
    sourceName: card.source,
    title: card.documentTitle,
    date: card.date,
    quote: card.quote,
    url: card.canonicalLink,
    context: card.context,
    meta: {
      effectiveDate: card.effectiveDate,
      amendedBy: card.amendedBy,
      supersededBy: card.supersededBy,
    },
    stance: 'mixed',
  };
}

/**
 * Convert API QuoteCard to LegacyQuoteCard for UI compatibility
 */
export function toLegacyQuoteCard(card: QuoteCard): LegacyQuoteCard {
  const laneLabels = {
    A: 'Lane A — Law & regulation',
    B: 'Lane B — Official context',
    C: 'Lane C — Public data',
  };

  return {
    id: card.id,
    quote: card.quote,
    source: card.sourceName,
    documentTitle: card.title,
    date: card.date,
    canonicalLink: card.url,
    lane: card.lane,
    laneLabel: laneLabels[card.lane],
    context: card.context,
    effectiveDate: (card.meta?.effectiveDate as string) || card.date,
    amendedBy: card.meta?.amendedBy as string,
    supersededBy: card.meta?.supersededBy as string,
    stance: card.stance,
  };
}
